import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import ResponsiveNavigation from '../../components/ResponsiveNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Check, X, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Building2 } from 'lucide-react';

const JobastiAdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [analytics, setAnalytics] = useState({
    licensesSoldThisMonth: 0,
    turnoverCents: 0,
    currency: 'EUR',
    licenseSalesThisMonth: 0,
    newCompaniesThisMonth: 0,
  });
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: '',
    organizationId: ''
  });
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const currentDate = new Date().toLocaleDateString('de-DE');
  const timeSlots = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00"];
  
  // Get current time for the red line indicator
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();

  const TODO_KEY = `jobasti_todos_${userProfile?.id}`;
  const [todoItems, setTodoItems] = useState([]);
  const [newTodoText, setNewTodoText] = useState('');
  const [showTodoInput, setShowTodoInput] = useState(false);
  const MAX_TODO_WORDS = 30;

  // ── @company mention state ───────────────────────────────────
  // Fetched once on mount; used to power the picker.
  const [companies, setCompanies] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [pendingTags, setPendingTags] = useState([]); // [{ companyId, name }]
  const todoInputRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/companies-lite`)
      .then((r) => setCompanies(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => setCompanies([]));
  }, []);

  // Load todos from DB when userProfile is available (synced across browsers/devices)
  useEffect(() => {
    if (userProfile?.id) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/todos`, { params: { userId: userProfile.id } })
        .then((r) => setTodoItems(Array.isArray(r.data?.todos) ? r.data.todos : []))
        .catch(() => setTodoItems([]));
    }
  }, [userProfile?.id]);

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarProvider, setCalendarProvider] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', time: '10:00', duration: '30' });
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchOrganizations();
    if (userProfile?.id) {
      fetchCalendar(selectedDate);
    }
  }, [userProfile?.id, selectedDate]);

  const fetchCalendar = async (dateForRange = new Date()) => {
    if (!userProfile?.id) return;
    try {
      setCalendarLoading(true);
      const statusRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/status`,
        { params: { userId: userProfile.id } }
      );
      const connected = !!statusRes.data?.connected;
      setCalendarConnected(connected);
      setCalendarProvider(statusRes.data?.provider || null);
      if (!connected) {
        setCalendarEvents([]);
        return;
      }
      const start = new Date(dateForRange); start.setHours(0,0,0,0);
      const end = new Date(start); end.setHours(23,59,59,999);
      const evRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/events`,
        { params: { userId: userProfile.id, timeMin: start.toISOString(), timeMax: end.toISOString() } }
      );
      const formatted = (evRes.data?.events || []).map(e => {
        const d = new Date(e.start);
        return {
          time: `${String(d.getHours()).padStart(2,'0')}:00`,
          title: e.title || 'Untitled event',
          meetLink: e.meetLink,
          startsAt: d,
        };
      });
      setCalendarEvents(formatted);
    } catch (err) {
      console.error('Admin calendar fetch failed', err);
      setCalendarEvents([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleConnect = async (provider = 'google') => {
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/auth-url`,
        { params: { userId: userProfile.id, provider } }
      );
      if (r.data?.authUrl) window.location.href = r.data.authUrl;
    } catch (e) {
      alert('Failed to start calendar connect. Open Settings to retry.');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/dashboard`);
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/companies`);
      if (response.data.success) {
        setOrganizations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.role) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    if (inviteForm.role === 'client_recruiter' && !inviteForm.organizationId) {
      setMessage({ type: 'error', text: 'Please select an organization for the recruiter' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invite`, inviteForm);
      
      if (response.data.success) {
        const registrationUrl = response.data.data.invitation.registrationUrl;
        const successMessage = response.data.message;
        
        setMessage({ 
          type: 'success', 
          text: successMessage,
          registrationUrl: registrationUrl
        });
        setInviteForm({ email: '', role: '', organizationId: '' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to send invitation' 
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Registration link copied to clipboard!');
  };

  const saveTodos = (items) => {
    setTodoItems(items);
    if (!userProfile?.id) return;
    axios
      .put(`${process.env.REACT_APP_BACKEND_URL}/api/todos`, { todos: items }, { params: { userId: userProfile.id } })
      .catch((err) => console.warn('Failed to save todos:', err));
  };

  const toggleTodo = (id) => {
    const updated = todoItems.map(item =>
      item.id === id ? { ...item, completed: !item.completed } : item
    );
    saveTodos(updated);
  };

  const removeTodo = (id) => {
    // Best-effort: also delete the global company_tags this TODO created.
    const removed = todoItems.find((t) => t.id === id);
    if (removed?.companyTags?.length && userProfile?.id) {
      const backend = process.env.REACT_APP_BACKEND_URL;
      removed.companyTags.forEach((ct) => {
        axios
          .delete(`${backend}/api/admin/companies/${ct.companyId}/tags`, {
            params: { actorUserId: userProfile.id, todoId: id },
          })
          .catch(() => { /* ignore — non-blocking */ });
      });
    }
    saveTodos(todoItems.filter(item => item.id !== id));
  };

  const addTodo = () => {
    const text = newTodoText.trim();
    if (!text) return;
    const newId = Date.now();
    // Dedupe: only keep unique companyIds among the pending tags.
    const seen = new Set();
    const tagsForItem = pendingTags.filter((t) => {
      if (seen.has(t.companyId)) return false;
      seen.add(t.companyId); return true;
    });
    const newItem = { id: newId, text, completed: false, companyTags: tagsForItem };
    saveTodos([...todoItems, newItem]);

    // Persist tags globally so every Jobasti admin sees them.
    if (tagsForItem.length && userProfile?.id) {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const actorName = [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ')
        || userProfile.email || 'A Jobasti admin';
      tagsForItem.forEach((ct) => {
        axios
          .post(`${backend}/api/admin/companies/${ct.companyId}/tags`, {
            actorUserId: userProfile.id,
            actorName,
            todoText: text,
            todoId: newId,
          })
          .catch(() => { /* ignore — non-blocking */ });
      });
    }

    setNewTodoText('');
    setPendingTags([]);
    setMentionOpen(false);
    setMentionQuery('');
    setShowTodoInput(false);
  };

  const handleTodoInput = (value) => {
    const words = value.split(/\s+/).filter(Boolean);
    if (words.length > MAX_TODO_WORDS) return;
    setNewTodoText(value);
    // Detect an open @mention at the caret: last "@…" token in the text.
    const m = value.match(/@([^\s@]{0,40})$/);
    if (m) {
      setMentionQuery(m[1] || '');
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
      setMentionQuery('');
    }
  };

  const selectMention = (company) => {
    // Replace the trailing "@query" with "@CompanyName " in the input.
    const replaced = newTodoText.replace(/@([^\s@]{0,40})$/, `@${company.name} `);
    setNewTodoText(replaced);
    setPendingTags((prev) => (prev.some((t) => t.companyId === company.id) ? prev : [...prev, { companyId: company.id, name: company.name }]));
    setMentionOpen(false);
    setMentionQuery('');
    // Return focus to the input.
    setTimeout(() => todoInputRef.current?.focus(), 0);
  };

  const filteredCompanies = mentionOpen
    ? companies
        .filter((c) => c.name.toLowerCase().includes(mentionQuery.toLowerCase()))
        .slice(0, 8)
    : [];

  const todoWordCount = newTodoText.trim().split(/\s+/).filter(Boolean).length;

  // Calculate red line position based on current time (for hours 9-18)
  const getRedLineIndex = () => {
    if (currentHour < 9 || currentHour > 18) return -1;
    return currentHour - 9;
  };

  const redLineIndex = getRedLineIndex();

  const handleCreateEvent = async () => {
    if (!newEvent.title.trim() || !userProfile?.id) return;
    try {
      setSavingEvent(true);
      const [h, m] = newEvent.time.split(':').map(Number);
      const start = new Date(selectedDate); start.setHours(h, m, 0, 0);
      const end = new Date(start.getTime() + parseInt(newEvent.duration, 10) * 60000);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/events`, {
        userId: userProfile.id,
        title: newEvent.title.trim(),
        start: start.toISOString(),
        end: end.toISOString(),
        description: 'Created from Super Admin dashboard',
        attendees: [],
      });
      setShowAddEvent(false);
      setNewEvent({ title: '', time: '10:00', duration: '30' });
      fetchCalendar(selectedDate);
    } catch (err) {
      console.error('Create event failed', err);
    } finally {
      setSavingEvent(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <ResponsiveNavigation />
      
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">{t('appNav.dashboard')}</h1>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Calendar (same as recruiter) */}
          <div>
            <Card className="h-full">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg font-semibold">Calendar</CardTitle>
                  <div className="flex items-center gap-2">
                    {calendarConnected && calendarProvider && (
                      <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded" data-testid="admin-cal-provider">{calendarProvider}</span>
                    )}
                    <Button
                      variant="link"
                      className="text-blue-500 text-sm p-0 h-auto font-normal inline-flex items-center gap-1"
                      onClick={() => calendarConnected ? setShowAddEvent(true) : navigate('/settings')}
                      data-testid="admin-cal-add-event"
                    >
                      {calendarConnected ? <><Plus className="w-3.5 h-3.5" /> add event</> : '+ connect'}
                    </Button>
                  </div>
                </div>
                {calendarConnected && (
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); setSelectedDate(d); }}
                      className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                      data-testid="admin-cal-prev-day"
                      aria-label="Previous day"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setSelectedDate(new Date())}
                      className="text-sm text-gray-400 hover:text-blue-600 transition-colors"
                      data-testid="admin-cal-current-date"
                    >
                      {selectedDate.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </button>
                    <button
                      onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); setSelectedDate(d); }}
                      className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                      data-testid="admin-cal-next-day"
                      aria-label="Next day"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {!calendarConnected && <p className="text-sm text-gray-400">{currentDate}</p>}
              </CardHeader>
              <CardContent className="pt-0">
                {!calendarConnected ? (
                  <div className="py-8 text-center" data-testid="admin-cal-connect">
                    <CalendarIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-600 mb-1">Connect your calendar</p>
                    <p className="text-xs text-gray-400 mb-4">See your meetings here</p>
                    <div className="flex flex-col gap-2 max-w-xs mx-auto">
                      <Button onClick={() => handleConnect('google')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-connect-google">Connect Google Calendar</Button>
                      <Button onClick={() => handleConnect('outlook')} variant="outline" data-testid="admin-connect-outlook">Connect Outlook Calendar</Button>
                    </div>
                  </div>
                ) : (
                <div className="relative max-h-[320px] overflow-y-auto" id="admin-calendar-scroll" data-testid="admin-calendar-scroll">
                  {timeSlots.map((time, index) => {
                    const slotHour = time.split(':')[0];
                    const event = calendarEvents.find(e => e.time && e.time.split(':')[0] === slotHour);
                    const isRedLine = index === redLineIndex;
                    const isSelected = selectedEvent?.time === event?.time;
                    return (
                      <div key={time} className="relative">
                        <div className="flex items-center min-h-[36px]">
                          <span className="w-12 text-sm text-gray-400 flex-shrink-0">{time}</span>
                          <div className="flex-1 ml-3 border-b border-gray-300 h-full min-h-[36px] flex items-center relative">
                            {event && (
                              <div className="relative">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setSelectedEvent(isSelected ? null : event); }}
                                  className={`text-sm px-2 py-0.5 rounded cursor-pointer transition-colors truncate max-w-full ${
                                    isSelected ? 'text-white bg-blue-600' : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                                  }`}
                                  data-testid={`admin-event-${event.time}`}
                                >
                                  <span className="font-medium">{event.time}</span> {event.title}
                                </button>
                                {isSelected && (
                                  <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-xl p-3 text-sm w-[220px]" data-testid="admin-event-detail">
                                    <div className="flex justify-between items-start mb-2">
                                      <div className="min-w-0">
                                        <p className="font-semibold text-gray-800 text-xs leading-tight">{selectedEvent.title}</p>
                                      </div>
                                      <button onClick={(e) => { e.stopPropagation(); setSelectedEvent(null); }} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                    <div className="space-y-1 text-xs text-gray-600">
                                      <p><span className="text-gray-400">Time:</span> {selectedEvent.time}</p>
                                    </div>
                                    {selectedEvent.meetLink && (
                                      <a
                                        href={selectedEvent.meetLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-white rounded text-xs font-medium bg-blue-600 hover:bg-blue-700"
                                        data-testid="admin-event-join-link"
                                      >
                                        Join Meeting
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Red line indicator for current time (only when viewing today) */}
                        {isRedLine && selectedDate.toDateString() === new Date().toDateString() && (
                          <div id="admin-calendar-now-line" className="absolute left-12 right-0 flex items-center z-10" style={{ top: `${(currentMinutes / 60) * 36}px` }}>
                            <div className="w-2.5 h-2.5 bg-red-500 rounded-full -ml-1"></div>
                            <div className="flex-1 h-0.5 bg-red-500"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!calendarLoading && calendarEvents.length === 0 && (
                    <p className="text-center text-sm text-gray-400 py-4">No events scheduled for today</p>
                  )}
                  {calendarLoading && (
                    <p className="text-[11px] text-gray-400 text-center mt-2">Loading…</p>
                  )}
                </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Middle Column */}
          <div className="space-y-6">
            {/* Analytics Section */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">Analytics</CardTitle>
                <p className="text-sm text-gray-400">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center" data-testid="kpi-licenses-sold">
                    <div className="text-3xl font-bold text-gray-900">{analytics.licensesSoldThisMonth}</div>
                    <div className="text-sm text-gray-600 mt-1">Licenses sold (seats)</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center" data-testid="kpi-turnover">
                    <div className="text-3xl font-bold text-gray-900">
                      {(() => {
                        try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: analytics.currency || 'EUR', maximumFractionDigits: 0 }).format((analytics.turnoverCents || 0) / 100); }
                        catch { return `€${Math.round((analytics.turnoverCents || 0) / 100)}`; }
                      })()}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Revenue</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center" data-testid="kpi-sales-made">
                    <div className="text-3xl font-bold text-gray-900">{analytics.licenseSalesThisMonth}</div>
                    <div className="text-sm text-gray-600 mt-1">Sales made</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center" data-testid="kpi-new-companies">
                    <div className="text-3xl font-bold text-gray-900">{analytics.newCompaniesThisMonth}</div>
                    <div className="text-sm text-gray-600 mt-1">New companies</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Todo Section (same as recruiter) */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg font-semibold">To Do</CardTitle>
                  <Button
                    variant="link"
                    className="text-blue-500 text-sm p-0 h-auto font-normal"
                    onClick={() => setShowTodoInput(!showTodoInput)}
                    data-testid="admin-add-todo-btn"
                  >
                    + add task
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Add Todo Input */}
                {showTodoInput && (
                  <div className="mb-3 pb-3 border-b border-gray-100">
                    <div className="flex gap-2 relative">
                      <input
                        ref={todoInputRef}
                        type="text"
                        value={newTodoText}
                        onChange={e => handleTodoInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Escape') { setMentionOpen(false); setMentionQuery(''); return; }
                          if (e.key === 'Enter' && !mentionOpen) addTodo();
                        }}
                        placeholder="What needs to be done? Type @ to tag a company"
                        className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                        data-testid="admin-todo-input"
                      />
                      <Button
                        size="sm"
                        onClick={addTodo}
                        disabled={!newTodoText.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3"
                        data-testid="admin-submit-todo-btn"
                      >
                        Add
                      </Button>

                      {/* @company picker */}
                      {mentionOpen && filteredCompanies.length > 0 && (
                        <div
                          className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-y-auto"
                          data-testid="mention-picker"
                        >
                          {filteredCompanies.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); selectMention(c); }}
                              className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-blue-50"
                              data-testid={`mention-option-${c.id}`}
                            >
                              <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              <span className="text-sm text-gray-800 truncate">{c.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {mentionOpen && filteredCompanies.length === 0 && (
                        <div
                          className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-30 p-3 text-xs text-gray-500"
                          data-testid="mention-picker-empty"
                        >
                          No companies match &ldquo;{mentionQuery}&rdquo;
                        </div>
                      )}
                    </div>

                    {/* Tagged company chips */}
                    {pendingTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="pending-tags">
                        {pendingTags.map((t) => (
                          <span
                            key={t.companyId}
                            className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-[11px] font-medium px-2 py-0.5 rounded-full"
                          >
                            <Building2 className="w-3 h-3" />
                            {t.name}
                            <button
                              type="button"
                              onClick={() => setPendingTags((prev) => prev.filter((p) => p.companyId !== t.companyId))}
                              className="ml-1 hover:text-blue-900"
                              aria-label={`Remove tag ${t.name}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <p className={`text-xs mt-1 ${todoWordCount >= MAX_TODO_WORDS ? 'text-red-500' : 'text-gray-400'}`}>
                      {todoWordCount}/{MAX_TODO_WORDS} words · Type <span className="font-mono">@</span> to tag a company
                    </p>
                  </div>
                )}

                <div
                  className={`space-y-1 ${todoItems.length > 8 ? 'max-h-[380px] overflow-y-auto pr-1' : ''}`}
                  data-testid="admin-todos-list"
                >
                  {todoItems.length === 0 && !showTodoInput && (
                    <p className="text-sm text-gray-400 py-4 text-center">No tasks yet. Click &quot;+ add task&quot; to get started.</p>
                  )}
                  {todoItems.map((item) => (
                    <div key={item.id} className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
                      <button
                        onClick={() => toggleTodo(item.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          item.completed
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : 'border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {item.completed && <Check className="w-3 h-3" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm leading-relaxed ${item.completed ? 'text-gray-400' : 'text-gray-700'}`}>
                          {item.text}
                        </span>
                        {item.companyTags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.companyTags.map((t) => (
                              <span
                                key={t.companyId}
                                className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                                data-testid={`todo-tag-${item.id}-${t.companyId}`}
                              >
                                <Building2 className="w-3 h-3" />
                                {t.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeTodo(item.id)}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Invite User Form */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">Invite user</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleInviteSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role*
                    </label>
                    <select
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value, organizationId: '' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="invite-role-select"
                    >
                      <option value="">Select role</option>
                      <option value="prospect">Prospect (Candidate)</option>
                      <option value="client_recruiter">Client Recruiter</option>
                      <option value="client_admin">Client Admin</option>
                      <option value="sales_executive">Sales Executive (JobastiIQ)</option>
                      <option value="jobasti_admin">Jobasti Super Admin (cross-company)</option>
                    </select>
                  </div>

                  {(inviteForm.role === 'client_recruiter' || inviteForm.role === 'client_admin') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Organization*
                      </label>
                      <select
                        value={inviteForm.organizationId}
                        onChange={(e) => setInviteForm({ ...inviteForm, organizationId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select organization</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail*
                    </label>
                    <input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="user@example.com"
                      required
                    />
                  </div>

                  {message.text && (
                    <div className={`p-3 rounded-md ${
                      message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                    }`}>
                      <p className="mb-2">{message.text}</p>
                      {message.registrationUrl && (
                        <div className="mt-3 p-2 bg-white rounded border border-green-200">
                          <p className="text-sm font-medium mb-1">Registration Link:</p>
                          <div className="flex items-center gap-2">
                            <input 
                              type="text" 
                              value={message.registrationUrl} 
                              readOnly 
                              className="flex-1 text-xs p-2 bg-gray-50 rounded border"
                            />
                            <button
                              type="button"
                              onClick={() => copyToClipboard(message.registrationUrl)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-xs font-medium"
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Send Invitation'}
                  </button>
                </form>
              </CardContent>
            </Card>

            {/* Create Company Account */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">Create company account</CardTitle>
              </CardHeader>
              <CardContent>
                <button
                  onClick={() => navigate('/admin/companies/add')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Create
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Event Modal — mirrors the recruiter's quick-create flow */}
      {showAddEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="admin-cal-add-event-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add calendar event</h3>
              <button onClick={() => setShowAddEvent(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">On {selectedDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Title</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  placeholder="Meeting title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="admin-cal-new-event-title"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Time</label>
                  <input
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="admin-cal-new-event-time"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Duration (min)</label>
                  <select
                    value={newEvent.duration}
                    onChange={(e) => setNewEvent({ ...newEvent, duration: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="admin-cal-new-event-duration"
                  >
                    <option value="15">15</option>
                    <option value="30">30</option>
                    <option value="45">45</option>
                    <option value="60">60</option>
                    <option value="90">90</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddEvent(false)}>Cancel</Button>
              <Button
                onClick={handleCreateEvent}
                disabled={!newEvent.title.trim() || savingEvent}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="admin-cal-new-event-save"
              >
                {savingEvent ? 'Saving…' : 'Add to calendar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobastiAdminDashboard;
